import { useCallback, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useGT } from 'gt-react';
import { useToast } from '@/context/ToastContext';
import { INSERT_IMAGE_COMMAND } from './ImageNode';

export type UploadImageFn = (
  file: File,
) => Promise<{ src: string; documentId: string }>;

/**
 * Shared "upload a File, then insert it as an ImageNode" flow used by both the
 * toolbar's insert-image button and the paste/drop handler. Centralizing it
 * keeps the upload → INSERT_IMAGE_COMMAND → error-toast behavior identical
 * across entry points. `isUploading` is per-caller and drives the toolbar's
 * spinner; the paste path ignores it.
 */
export function useInsertUploadedImage(uploadImage?: UploadImageFn) {
  const [editor] = useLexicalComposerContext();
  const [isUploading, setIsUploading] = useState(false);
  const gt = useGT();
  const { showToast } = useToast();

  const insertImage = useCallback(
    async (file: File) => {
      if (!uploadImage) return;
      setIsUploading(true);
      try {
        const { src, documentId } = await uploadImage(file);
        editor.dispatchCommand(INSERT_IMAGE_COMMAND, { src, documentId });
      } catch (error) {
        console.error('Image upload failed:', error);
        showToast({
          title: gt('Image upload failed', { context: 'toast title' }),
          message:
            error instanceof Error
              ? error.message
              : gt('Could not upload the image. Please try again.', {
                  context: 'toast message',
                }),
          color: 'error',
          icon: 'x',
        });
      } finally {
        setIsUploading(false);
      }
    },
    [editor, uploadImage, gt, showToast],
  );

  return { insertImage, isUploading };
}
